import { Suspense, lazy, useEffect, useState } from 'react';

import toast from 'react-hot-toast';
import { TOAST_ARIA_PROPS } from 'utilities/utils';

import { usePostApiV5DocumentsSignedUrls } from '@liscio/api';
import {
  ReadOnlyPdfPreview,
  useReadOnlyPdfFallback,
  useSharedPdfViewerGate,
} from '@liscio/common';
import { PdfDocumentViewer } from '@liscio/ui';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';

import { CloseIcon } from 'common/Icons';
import PDFViewerErrorBoundary from 'common/PDFViewerErrorBoundary/PDFViewerErrorBoundary';
import { reportPdfViewerError } from 'common/PDFViewerErrorBoundary/reportPdfViewerError';

export type SignedUrlPDFDialogProps = {
  open: boolean;
  token: string;
  title: string;
  onClose: () => void;
  loadingCallback?: (loading: boolean) => void;
};

const PSPDFKitViewer = lazy(() => import('common/PSPDFKit/PSPDFViewer'));

const pdfViewerFallbackStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
};

const pdfViewerFallback = (
  <div
    role="status"
    aria-label="Loading PDF viewer"
    style={pdfViewerFallbackStyle}
  >
    Loading PDF viewer...
  </div>
);

/**
 * Dialog to display a PDF viewer with token -> signedUrl logic.
 */
export const SignedUrlPDFDialog = ({
  open,
  token,
  title,
  onClose,
  loadingCallback,
}: SignedUrlPDFDialogProps) => {
  // state
  const [startFileLoading, setStartFileLoading] = useState(false);
  const sharedViewer = useSharedPdfViewerGate(open ? token ?? null : null);
  const readOnlyFallback = useReadOnlyPdfFallback(open ? token ?? null : null);
  const [signedUrl, setSignedUrl] = useState('');

  // API calls
  const { mutateAsync: getSignedUrl, isLoading } =
    usePostApiV5DocumentsSignedUrls({
      mutation: {
        cacheTime: 0,
      },
    });

  useEffect(() => {
    if (open) {
      if (token) {
        getSignedUrl(
          {
            data: {
              disposition: 'inline',
              retrieval_token: token || '',
            },
          },
          {
            onSuccess: (data) => {
              setSignedUrl(data.signed_url || '');
            },
            onError: (error) => {
              // eslint-disable-next-line no-console
              console.error('Error getting signed URL:', error);
              onClose();
              toast.error('Error retrieving document. Please try again.', { ariaProps: TOAST_ARIA_PROPS });
            },
            onSettled: () => {
              setStartFileLoading(false);
            },
          },
        );
      }
    }
  }, [getSignedUrl, onClose, open, startFileLoading, token]);

  // Callback to parent component
  useEffect(() => {
    if (loadingCallback) {
      loadingCallback(isLoading);
    }
  }, [isLoading, loadingCallback]);

  if (!token) {
    return null;
  }

  let operation: 'loadPdfDocumentViewer' | 'loadReadOnlyPdfPreview' | 'loadPSPDFViewer' =
    'loadPSPDFViewer';
  if (sharedViewer) operation = 'loadPdfDocumentViewer';
  else if (readOnlyFallback) operation = 'loadReadOnlyPdfPreview';

  return (
    <Dialog
      open={open && !isLoading && !!signedUrl}
      fullScreen
      onClose={onClose}
      title={title}
      PaperProps={{
        sx: {
          width: '98vw',
          maxHeight: '98vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 1.25 }} id="customized-dialog-title">
        {title}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          height: '98%',
          ...((sharedViewer || readOnlyFallback) && { position: 'relative' }),
        }}
      >
        <PDFViewerErrorBoundary key={signedUrl} operation={operation}>
          {sharedViewer && (
            <PdfDocumentViewer
              documentId={token}
              title={title}
              url={signedUrl}
              onError={reportPdfViewerError}
            />
          )}
          {readOnlyFallback && (
            <ReadOnlyPdfPreview
              url={signedUrl}
              title={title}
              onError={reportPdfViewerError}
            />
          )}
          {!sharedViewer && !readOnlyFallback && (
            <Suspense fallback={pdfViewerFallback}>
              <PSPDFKitViewer fileName={title} url={signedUrl} />
            </Suspense>
          )}
        </PDFViewerErrorBoundary>
      </DialogContent>
    </Dialog>
  );
};
